import type { LeaseOverride } from '@/types/api'

/**
 * Returns the lease term query parameter key for the given apply provider.
 * Ported from Spaces 2 ControlFactory.prototype.getProviderLeaseTermParam.
 */
export function getProviderLeaseTermParam (provider: string): string | null {
	switch (provider) {
		case 'realpage':
		case 'realpage-phased':
			return 'LeaseTerm'
		case 'rentcafe':
		case 'yardi':
			return 'sLeaseTerm'
		case 'mri':
			return 'leaseTerm'
		case 'entrada':
			return 'term_month'
		default:
			return null
	}
}

/**
 * Returns the move-in date query parameter key for the given apply provider.
 * Derived from Spaces 2 set_apply_link (PHP) and bindControlLeaseTermCalendar (JS).
 */
export function getProviderMoveInDateParam (provider: string): string | null {
	switch (provider) {
		case 'entrada':
			return 'lease_start_date'
		case 'realpage':
		case 'realpage-phased':
		case 'rentcafe':
		case 'yardi':
		case 'mri':
		case 'voyager':
		case 'funnel':
			return 'MoveInDate'
		default:
			// custom, onsite, appfolio, none — no date param
			return null
	}
}

export type MoveInDateFormat = 'YYYY-MM-DD' | 'MM/DD/YYYY'

/**
 * Returns the move-in date format the given apply provider expects.
 * Mirrors the date formats used by PHP Unit::set_apply_link; entrada follows Spaces 2.
 */
export function getProviderMoveInDateFormat (provider: string): MoveInDateFormat {
	switch (provider) {
		case 'mri':
		case 'funnel':
		case 'entrada':
			return 'MM/DD/YYYY'
		default:
			return 'YYYY-MM-DD'
	}
}

/**
 * Converts an ISO (YYYY-MM-DD) move-in date to the format the provider expects.
 */
export function formatMoveInDateForProvider (isoDate: string, provider: string): string {
	if (getProviderMoveInDateFormat(provider) !== 'MM/DD/YYYY') return isoDate

	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
	if (!match) return isoDate

	return `${match[2]}/${match[3]}/${match[1]}`
}

/**
 * Sets or replaces a query parameter on a URL string.
 * Handles both absolute and relative (query-string-only) URLs.
 */
function setUrlParam (url: string, key: string, value: string): string {
	let result: string
	try {
		// Absolute URL
		const parsed = new URL(url)
		parsed.searchParams.set(key, value)
		result = parsed.toString()
	} catch {
		// Relative or query-string-only URL (e.g. "?spaces_tab=apply&...")
		const dummyBase = 'https://_'
		const fullUrl = url.startsWith('?') ? dummyBase + url : `${dummyBase}/${url}`
		const parsed = new URL(fullUrl)
		parsed.searchParams.set(key, value)
		// Strip the dummy base back out
		result = url.startsWith('?')
			? parsed.search + parsed.hash
			: parsed.pathname.slice(1) + parsed.search + parsed.hash
	}

	// Keep literal slashes (e.g. MM/DD/YYYY dates) to match the PHP-built URL.
	const encodedPair = new URLSearchParams({ [key]: value }).toString()
	return result.replace(encodedPair, encodedPair.replace(/%2F/gi, '/'))
}

/**
 * Builds the final apply URL given the unit's base apply_url, the apply provider,
 * and an optional lease override from user selection.
 *
 * - No override → returns baseUrl unchanged
 * - With override → sets/replaces lease term and move-in date params per provider rules
 */
export function buildApplyUrl (
	baseUrl: string,
	provider: string,
	leaseOverride?: LeaseOverride
): string {
	if (!baseUrl) return ''
	if (!leaseOverride) return baseUrl

	let url = baseUrl

	// Set lease term param if the provider supports it
	const leaseTermParam = getProviderLeaseTermParam(provider)
	if (leaseTermParam) {
		url = setUrlParam(url, leaseTermParam, String(leaseOverride.selectedTerm))
	}

	// Set move-in date param if the provider supports it
	const moveInDateParam = getProviderMoveInDateParam(provider)
	if (moveInDateParam && leaseOverride.moveInDate) {
		url = setUrlParam(url, moveInDateParam, formatMoveInDateForProvider(leaseOverride.moveInDate, provider))
	}

	return url
}

/**
 * Applies the minimum lease term to the base apply URL on initial render.
 * Replicates Spaces 2 addMinTermToApplyLinks — used when rev management is
 * enabled but the user hasn't made a lease term selection yet.
 */
export function getInitialApplyUrl (
	baseUrl: string,
	provider: string,
	minLeaseTerm?: string | number | null
): string {
	if (!baseUrl) return ''
	if (minLeaseTerm == null) return baseUrl

	const leaseTermParam = getProviderLeaseTermParam(provider)
	if (!leaseTermParam) return baseUrl

	return setUrlParam(baseUrl, leaseTermParam, String(minLeaseTerm))
}
